import React, { useMemo } from 'react';
import uniq from 'lodash/uniq';
import type { PreloadedQuery } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import type { versionMenuQuery } from '@atlassian/jira-relay/src/__generated__/versionMenuQuery.graphql';
import {
	type MenuNode,
	type MenuItemNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { BACKLOG_PACKAGE_NAME } from '../../../../../../common/constants/analytics.tsx';
import type { CardId } from '../../../../../../model/types.tsx';
import { useBacklogSelector } from '../../../../../../state/index.tsx';
import { getCards } from '../../../../../../state/selectors/cards/index.tsx';
import { VERSION_MENU_ID } from '../../constants.tsx';
import { VersionMenu } from './version-menu/index.tsx';
import type { VersionMenuProps } from './version-menu/types.tsx';

export const VersionMenuWrapper = (versionMenuProps: VersionMenuProps) => (
	<JSErrorBoundary packageName={BACKLOG_PACKAGE_NAME} id="jira.backlog.context-menu.version">
		{/* eslint-disable-next-line jira/restricted/react-suspense */}
		<React.Suspense fallback={null}>
			<VersionMenu {...versionMenuProps} />
		</React.Suspense>
	</JSErrorBoundary>
);
const EMPTY_ARR: MenuNode[] = [];

export const useVersion = ({
	selectedCardIds,
	versionMenuQueryRef,
}: {
	selectedCardIds: CardId[];
	versionMenuQueryRef: PreloadedQuery<versionMenuQuery> | null | undefined;
}): MenuNode[] => {
	const cards = useBacklogSelector(getCards);

	return useMemo(() => {
		if (!versionMenuQueryRef) return EMPTY_ARR;

		const selectedCards = selectedCardIds.map((cardId) => cards[cardId]).filter(Boolean);

		// Selected cards are valid if they belong to the same project
		const isCardSelectionValid = uniq(selectedCards.map((card) => card.projectId)).length === 1;
		if (!isCardSelectionValid) return EMPTY_ARR;

		const menuItemNode: MenuItemNode = {
			type: MenuNodeType.Node,
			id: `${VERSION_MENU_ID}/node`,
			node: (menuItemNodeArgs) => (
				<VersionMenuWrapper
					selectedCardIds={selectedCardIds}
					selectedCards={selectedCards}
					versionMenuQueryRef={versionMenuQueryRef}
					menuItemNodeArgs={menuItemNodeArgs}
				/>
			),
		};
		return [menuItemNode];
	}, [cards, selectedCardIds, versionMenuQueryRef]);
};
